import { unreadable } from './shared.ts';

/**
 * §8's Channel page.
 *
 * The **tab labels** are also nouns inside sentences ("This channel has no Shorts tab"), which is why
 * the sentences take the label as a parameter rather than embedding it: a language that inflects the
 * noun needs the sentence to own the grammar. Every **empty** sentence is still doubled — an empty tab
 * the channel declares and an empty tab it does not have are the same list for different reasons — but
 * the *detail* lines no longer explain the difference: both now say only that there is nothing
 * there, since the titles above them already draw the distinction and the explanation was three clauses
 * about why a page will look the same tomorrow.
 */
export const channel = {
  tab: {
    videos: 'Videos',
    shorts: 'Shorts',
    streams: 'Live',
    playlists: 'Playlists',
    about: 'About',
  },

  /**
   * The same four sections as **nouns inside a sentence**, which is not the same list: the strip
   * says "Live" because that is a tab's name in two syllables, and "There are no Live on this channel"
   * is not a sentence. Only `Shorts` keeps its capital, being YouTube's proper noun for the format.
   */
  tabNoun: {
    videos: 'videos',
    shorts: 'Shorts',
    streams: 'live streams',
    playlists: 'playlists',
  },

  badge: {
    autoGenerated: 'Auto-generated by YouTube',
    ageGated: 'Age-restricted',
  },

  sort: {
    label: 'Sort',
    newest: 'Newest',
    oldest: 'Oldest',
    popular: 'Most viewed',
  },

  notice: {
    /*
     * Plain text, not markdown: `Notice` renders its children and nothing else, and the first draft of
     * this sentence shipped a literal `**live right now**` to the screen — found by reading the page
     * rather than the code. Emphasis has to be markup or nothing, and here it is nothing.
     *
     * Kept where the neighbouring notices were dropped, because this one is about the *videos*: a
     * stream that is live right now is indistinguishable here from one that is not, so the page cannot
     * draw a LIVE badge and says why rather than looking wrong.
     */
    streamsLiveUnreportedOne:
      'One of these streams may be live right now. This list gives no way to tell, so no LIVE badge is drawn on it. Open it to see.',
    streamsLiveUnreported: (count: number): string =>
      `${count} of these streams may be live right now. This list gives no way to tell, so no LIVE badge is drawn on them. Open one to see.`,
  },

  allDropped: {
    title: 'Could not read this tab',
    detail: 'The instance answered, and nothing in it could be shown.',
  },

  empty: {
    videos: {
      title: 'No videos',
      detail: 'There are no videos on this channel.',
    },
    shorts: {
      title: 'No Shorts',
      detail: 'This channel has not published any, or YouTube did not serve the tab.',
    },
    streams: {
      title: 'No streams',
      detail: 'Nothing live and no past broadcasts. Ended streams normally stay listed here.',
    },
    playlists: {
      title: 'No public playlists',
      detail: 'Private playlists are not visible to anyone but their owner, signed in or not.',
    },
    /** The tab the channel does not have — reached from a typed URL, and empty by construction. */
    undeclaredTitle: (tabLabel: string): string => `This channel has no ${tabLabel} tab`,
    undeclaredDetail: (tabNoun: string): string => `There are no ${tabNoun} on this channel.`,
  },

  page: {
    verified: 'Verified',
    subscribers: (count: string): string => `${count} subscribers`,
    tabsLabel: 'Channel sections',
    /** The tab name is a noun inside the sentence, so the sentence takes it. */
    loadingTab: (tab: string): string => `Loading ${tab} on this channel…`,
    gridLabel: (tab: string): string => `${tab} on this channel`,
    loadMore: (soFar: number): string => `Load more (${soFar} so far)`,
    loadingMore: 'Loading…',
    loadingAbout: 'Loading this channel’s description…',
    noDescription: 'This channel has no description.',
    totalViewsLabel: 'Total views',
    keywords: 'Keywords',
    /** §8's jump-out, in the About tab's own verb — a channel is viewed, not watched. */
    jumpOut: {
      label: 'View on',
      targets: {
        youtube: 'YouTube',
        instance: 'Invidious',
      },
    },
  },

  /** The in-channel search — server-side, unlike the grids' display-only searches. */
  search: {
    label: 'Search this channel',
    placeholder: 'Search this channel',
    clear: 'Clear the search',
    /** The collapsed icon's name while results are on screen — what the page is showing. */
    activeTitle: (q: string): string => `Searching this channel for “${q}”`,
    submit: 'Search',
    /** The results heading — what the list below is, since no tab is current while searching. */
    resultsFor: (q: string): string => `Results for “${q}”`,
    gridLabel: 'Search results on this channel',
    loading: 'Searching this channel…',
    emptyTitle: 'Nothing found',
    emptyDetail: (q: string): string =>
      `This channel has nothing matching “${q}”. YouTube answers this search, so a new upload can change that.`,
  },

  problem: {
    notFound: {
      title: 'No such channel',
      detail:
        'The instance cannot see a channel with this id. It never existed, or it was deleted or terminated.',
      retryable: false,
    },
    unreadable: unreadable('Could not read this tab'),
    /** The one subject where `upstream-broken` has a specific, reachable cause worth naming. */
    upstreamBroken: {
      title: 'YouTube will not serve this channel',
      detail:
        'Your instance asked and got an error back instead of a page. Auto-generated "- Topic" channels answer this way every time, and so do a few channels with an unusual layout. Trying again gets the same answer.',
      retryable: false,
    },
  },
} as const;
